import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ScheduleIcon from '@mui/icons-material/Schedule';
import NoCrashIcon from '@mui/icons-material/NoCrash';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Box,
  Button,
  FormGroup,
  IconButton,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { deleteEstimate } from '../../api/estimatesAPI';
import { deleteService } from '../../api/servicesAPI';
import { deleteVehicle, getVehicle, updateVehicle } from '../../api/vehiclesAPI';
import NavBar from '../layout/NavBar';
import EstimateModal from '../modals/EstimateModal';
import ServiceModal from '../modals/ServiceModal';
import { formatDateTime, formatDecimal, formatMileage } from '../../utils/dateUtils';

export default function ViewVehicle() {
  // Grab vehicle ID from URL param
  let { id: vehicleId } = useParams();
  const navigate = useNavigate();

  // Vehicle info from fetch call
  const [vehicle, setVehicle] = useState([]);

  // Vehicle modal states
  const [isVehicleModalOpen, setIsVehicleModalOpen] = useState(false);
  const handleVehicleModalOpen = () => setIsVehicleModalOpen(true);
  const handleVehicleModalClose = () => setIsVehicleModalOpen(false);

  // Estimate modal states
  const [isEstimateModalOpen, setIsEstimateModalOpen] = useState(false);
  const handleEstimateModalOpen = (estimate = null ) => {
    setEstimate(estimate);
    setIsEstimateModalOpen(true);
  }
  const handleEstimateModalClose = () => setIsEstimateModalOpen(false);

  // Service modal states
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const handleServiceModalOpen = (estimate = null, service = null) => {
    setEstimate(estimate);
    setServiceId(service);
    setIsServiceModalOpen(true);
  }
  const handleServiceModalClose = () => {
    setIsServiceModalOpen(false);
  }

  // Estimate and service data to pass to modals
  const [estimate, setEstimate] = useState(null);
  const [serviceId, setServiceId] = useState(null);

  // If true it will show services table but if false it will show estimates table
  const [isShowingServices, setIsShowingServices] = useState(true);

  // State variables for vehicle form
  const [vehicleForm, setVehicleForm] = useState({
    make: '',
    model: '',
    year: '',
    color: '',
    license: '',
    vin: '',
    notes: '',
  });

  // Add error states
  const [editVehicleErrors, setEditVehicleErrors] = useState({});
  const [addServiceErrors, setAddServiceErrors] = useState({});

  /** Update a specific field in the vehicle form. */
  const handleFieldChange = (field, value) => {
    setVehicleForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /** GET vehicle and set states. */
  const handleVehicleGet = async () => {
    try {
      const response = await getVehicle(vehicleId);
      const vehicleData = response.data;

      setVehicle(vehicleData);
      setVehicleForm({
        make: vehicleData.make,
        model: vehicleData.model,
        year: vehicleData.year,
        color: vehicleData.color,
        license: vehicleData.license,
        vin: vehicleData.vin,
        notes: vehicleData.notes,
      });
    } catch (error) {
      console.error(error);
    }
  }

  /** PUT vehicle, refresh vehicle data, and close edit vehicle modal. */
  const handleVehicleUpdate = async () => {
    try {
      await updateVehicle(vehicleId, vehicleForm);
      await handleVehicleGet();
      handleVehicleModalClose();
      setEditVehicleErrors({});
    } catch (error) {
      console.error(error);
      if (error.response && error.response.data) {
        setEditVehicleErrors(error.response.data);
      }
    }
  }

  /** DELETE vehicle and redirect to vehicles page. */
  const handleVehicleDelete = async () => {
    try {
      // Verify deletion of vehicle
      const confirmation = window.confirm('Are you sure you want to delete this vehicle?\nThis action cannot be undone.');
      if (!confirmation) {
        return;
      }
      await deleteVehicle(vehicleId);
      window.location = process.env.REACT_APP_BASE_URL + 'vehicles';
    } catch (error) {
      console.error(error);
    }
  };

  /** DELETE estimate and refresh vehicle data. */
  const handleEstimateDelete = async (id) => {
    try {
      // Verify deletion of estimate
      const confirmation = window.confirm(
        'Are you sure you want to delete this estimate?\n' +
        'Services associated with this estimate will not be deleted.\n' +
        'This action cannot be undone.');
      if (!confirmation) {
        return;
      }
      await deleteEstimate(id);
      await handleVehicleGet();
    } catch (error) {
      console.error(error);
    }
  };

  const isServiceCompleted = (service) =>
    String(service?.status ?? '').trim().toLowerCase() === 'completed';

  const warnIfCompletedService = (service, actionLabel) => {
    if (isServiceCompleted(service)) {
      const confirmed = window.confirm(
        `This service has already been marked as completed.\n\n` +
        `Editing will delete the signature.\n\n` +
        `Are you sure you want to ${actionLabel} it?`
      );
      return !confirmed;
    }
    return false;
  };

  /** DELETE service and refresh vehicle data. */
  const handleServiceDelete = async (id) => {
    try {
      // Verify deletion of service
      const confirmation = window.confirm('Are you sure you want to delete this service?\nThis action cannot be undone.');
      if (!confirmation) {
        return;
      }
      await deleteService(id);
      await handleVehicleGet();
    } catch (error) {
      console.error(error);
    }
  };

  const handleOpenCompleteService = async (service) => {
    if (warnIfCompletedService(service, 'complete')) {
      return;
    }

    navigate('/services/' + service.id, {
      state: {
        openCompleteDialog: true,
        returnTo: '/vehicles/' + vehicleId,
      },
    });
  };

  // Update vehicle data on component mount
  useEffect(() => {
    const fetchData = async () => {
      await handleVehicleGet();
    };
    fetchData();
  }, []);

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 550,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
  };

  return (
    <Box sx={{ textAlign: 'center', minWidth: '519px', maxWidth: '75%', margin: 'auto' }}>
      <Typography variant='h2'>Vehicle Detail</Typography>
      <NavBar active='Vehicles' />
      <Typography sx={{ mt:3, mb: 1, fontWeight: 'bold' }}>{vehicle.year} {vehicle.make} {vehicle.model} {vehicle.color ? `(${vehicle.color})` : ''}</Typography>
      <Typography sx={{ my: 1, fontWeight: 'bold' }}>
        Owners:{' '}
        {vehicle.list_owners_object &&
          vehicle.list_owners_object.map((owner, index) => (
            <span key={owner.id}>
              {index > 0 && ', '}
              <Link
                to={'/customers/' + owner.id}
                style={{ color: 'inherit', textDecoration: 'none' }}
              >
                {owner.name}
              </Link>
            </span>
          ))}
      </Typography>
      <Box sx={{ display:'Flex', justifyContent:'center', gap: '5px', my: 1 }}>
        <Button variant='outlined' onClick={handleVehicleModalOpen}>Edit</Button>
        <Button variant='outlined' color='error' onClick={handleVehicleDelete}>Delete</Button>
      </Box>
      <Typography sx={{ my:1, fontWeight: 'bold' }}>VIN: {vehicle.vin ? vehicle.vin : 'n/a'}</Typography>
      <Typography sx={{ my:1, fontWeight: 'bold' }}>License: {vehicle.license ? vehicle.license : 'n/a'}</Typography>
      <Typography sx={{ my:1, fontWeight: 'bold' }}>Notes: {vehicle.notes ? vehicle.notes : 'None'}</Typography>

      {/* Edit Vehicle Modal */}
      <Modal
        open={isVehicleModalOpen}
        onClose={handleVehicleModalClose}
        aria-labelledby='vehicle-modal-title'
        aria-describedby='vehicle-modal-description'
      >
        <Box sx={style}>
          <Typography id='vehicle-modal-title' variant='h6' component='h2'>
            Edit Vehicle
          </Typography>
          <FormGroup>
            <TextField
              required id='make'
              label='Make'
              variant='outlined'
              value={vehicleForm.make}
              onChange={(e) => handleFieldChange('make', e.target.value)}
              sx={{ my: 1 }}
              error={!!editVehicleErrors.make}
              helperText={editVehicleErrors.make}
            />
            <TextField
              required id='model'
              label='Model'
              variant='outlined'
              value={vehicleForm.model}
              onChange={(e) => handleFieldChange('model', e.target.value)}
              sx={{ mb: 1 }}
              error={!!editVehicleErrors.model}
              helperText={editVehicleErrors.model}
            />
            <TextField
              required id='year'
              label='Year'
              variant='outlined'
              value={vehicleForm.year}
              onChange={(e) => handleFieldChange('year', e.target.value)}
              sx={{ mb: 1 }}
              error={!!editVehicleErrors.year}
              helperText={editVehicleErrors.year}
            />
            <TextField
              id='color'
              label='Color'
              variant='outlined'
              value={vehicleForm.color}
              onChange={(e) => handleFieldChange('color', e.target.value)}
              sx={{ mb: 1 }}
              error={!!editVehicleErrors.color}
              helperText={editVehicleErrors.color}
            />
            <TextField
              id='license'
              label='License'
              variant='outlined'
              value={vehicleForm.license}
              onChange={(e) => handleFieldChange('license', e.target.value)}
              sx={{ mb: 1 }}
              error={!!editVehicleErrors.license}
              helperText={editVehicleErrors.license}
            />
            <TextField
              id='vin'
              label='VIN'
              variant='outlined'
              value={vehicleForm.vin}
              onChange={(e) => handleFieldChange('vin', e.target.value)}
              sx={{ mb: 1 }}
              error={!!editVehicleErrors.vin}
              helperText={editVehicleErrors.vin}
            />
            <TextField
              id='notes'
              label='Vehicle Notes'
              variant='outlined'
              multiline
              rows={4}
              value={vehicleForm.notes}
              onChange={(e) => handleFieldChange('notes', e.target.value)}
              sx={{ mb: 1 }}
              error={!!editVehicleErrors.notes}
              helperText={editVehicleErrors.notes}
            />
            <Button variant='contained' onClick={handleVehicleUpdate}>Submit</Button>
          </FormGroup>
        </Box>
      </Modal>
      {/* Modal end for editing vehicle */}

      {/* Estimate and Service Modals*/}
      <EstimateModal
          isOpen={isEstimateModalOpen}
          onClose={handleEstimateModalClose}
          vehicleId={vehicleId}
          estimate={estimate}
          owners={vehicle.list_owners_object}
          refreshFunction={handleVehicleGet}
      />

      <ServiceModal
          isOpen={isServiceModalOpen}
          onClose={handleServiceModalClose}
          vehicleId={vehicleId}
          refreshFunction={handleVehicleGet}
          estimate={estimate}
          owners={vehicle.list_owners_object}
          serviceId={serviceId}
      />
      {/* End of Estimate and Service Modals*/}

      {/* Vehicle Estimates and Services Table Heading */}
      <Box onClick={()=> setIsShowingServices(!isShowingServices)} sx={{ cursor:'pointer', mt: 1, mb: 1 }}>
        {!isShowingServices && <Typography variant='h4'>
          <strong>Estimates</strong>
          <span style={{color:'gray'}}> | Services</span>
        </Typography>}
        {isShowingServices && <Typography variant='h4'>
          <span style={{color:'gray'}}>Estimates | </span>
          <strong>Services</strong></Typography>}

      </Box>
      {/* End of estimates and services table heading */}

      {/* Estimates Table */}
      {!isShowingServices && <Box>
        <Button variant='outlined' sx={{ mb: 1 }} onClick={() => handleEstimateModalOpen()}>Create Estimate</Button>
        <TableContainer container={Paper} sx={{ textAlign: 'center' }}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Last Modified</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Parts</TableCell>
                <TableCell>Labor</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Scheduled</TableCell>
                <TableCell>Prepared For</TableCell>
                <TableCell sx={{ paddingLeft: '24px' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vehicle.estimates && vehicle.estimates.map(estimate => (
                <TableRow key={estimate.id} id={estimate.id}>
                  <TableCell>{formatDateTime(estimate.updated_at)}</TableCell>
                  <TableCell>{estimate.estimate_items_str}</TableCell>
                  <TableCell>${formatDecimal(estimate.parts_total)}</TableCell>
                  <TableCell>${formatDecimal(estimate.labor_total)}</TableCell>
                  <Tooltip title={`$${formatDecimal(estimate.estimate_total)}`}>
                   <TableCell>
                     ${formatDecimal(
                       estimate.estimate_subtotal ?? 0
                     )}
                     {estimate.estimate_owner_details.tax_exempt ? ' (Tax Exempt)' : ' + Tax'}
                   </TableCell>
                  </Tooltip>
                  <TableCell>
                    {Array.isArray(estimate.scheduled) && estimate.scheduled[0]
                      ? `${formatDateTime(estimate.scheduled[0])} (${estimate.scheduled[1]})`
                      : 'Not Scheduled'}
                  </TableCell>
                   <TableCell>{estimate.estimate_owner_details.name}</TableCell>
                  <TableCell>
                    <Tooltip title={!Array.isArray(!estimate.scheduled) ? 'Schedule Estimate' : 'Schedule Estimate (Disabled - Already Scheduled)'}>
                      <span>
                        <IconButton disabled={Array.isArray(estimate.scheduled)} onClick={() => handleServiceModalOpen(estimate)}>
                          <ScheduleIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={!Array.isArray(estimate.scheduled) ? 'Edit Estimate' : 'Edit Estimate (Disabled - Already Scheduled)'}>
                      <span>
                        <IconButton disabled={Array.isArray(estimate.scheduled)} onClick={() => handleEstimateModalOpen(estimate)}>
                          <EditIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title='View Estimate'>
                      <Link to={'/estimates/' + estimate.id}>
                        <IconButton size='small'>
                          <VisibilityIcon size='small'/>
                        </IconButton>
                      </Link>
                    </Tooltip>
                    <Tooltip title='Delete Estimate'>
                      <IconButton  onClick={() => handleEstimateDelete(estimate.id)} >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {vehicle.estimates && vehicle.estimates.length == 0 && (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center' }}>
                      No estimates found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>}
      {/* End of estimates table */}

      {/* Services Table */}
      {isShowingServices && <Box>
        <Button variant='outlined' sx={{ mb: 1 }} onClick={() => handleServiceModalOpen()}>Schedule Services</Button>
        <TableContainer container={Paper} sx={{ textAlign: 'center' }}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Service Date & Time</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Estimated Time</TableCell>
                <TableCell>Mileage</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vehicle.services && vehicle.services.map(service => (
                <TableRow key={service.id}>
                  <TableCell>{formatDateTime(service.datetime)}</TableCell>
                  <TableCell>{service.service_items_str}</TableCell>
                  <TableCell>{service.estimated_time}</TableCell>
                  <TableCell>{service.mileage ? formatMileage(service.mileage) : 'n/a'}</TableCell>
                  <Tooltip title={`$${formatDecimal(service.service_total)}`}>
                    <TableCell>
                      {service.service_subtotal !== null && service.service_subtotal !== undefined && service.service_subtotal !== ''
                        ? '$' + formatDecimal(service.service_subtotal) + ' + Tax'
                        : 'n/a'}
                    </TableCell>
                  </Tooltip>
                  <TableCell>{service.status ? service.status : 'n/a'}</TableCell>
                  <TableCell>
                    <Tooltip title='Complete Service'>
                      <IconButton onClick={() => handleOpenCompleteService(service)}>
                        <NoCrashIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Edit Service'>
                      <IconButton onClick={() => {
                        if (warnIfCompletedService(service, 'edit')) {
                          return;
                        }
                        handleServiceModalOpen(null, service.id);
                      }} >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='View Service'>
                      <Link to={'/services/' + service.id}>
                        <IconButton>
                          <VisibilityIcon />
                        </IconButton>
                      </Link>
                  </Tooltip>
                  <Tooltip title='Delete Service'>
                    <span>
                      <IconButton onClick={() => handleServiceDelete(service.id)} >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {vehicle.services && vehicle.services.length == 0 && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center' }}>
                      No services found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>}
      {/* End of services table */}
    </Box>
  );
}